<template>
  <div class="portfolio-tab">
    <div v-if="loading" class="pf-loading">
      <scale-loader color="#3d6b9a"></scale-loader>
    </div>

    <template v-else>
      <!-- Barra superior: subtítulo (izq) + controles alineados a la derecha. -->
      <div class="pf-toolbar">
        <span class="pf-subtitle">{{ $t('prm_portfolio_subtitle') }}</span>

        <div class="pf-controls">
          <div class="pf-search">
            <b-icon pack="fas" icon="magnifying-glass" size="is-small"></b-icon>
            <input v-model="search" type="text" :placeholder="$t('prm_portfolio_search')" />
          </div>

          <!-- Filtro por grupo (línea de negocio): pastillas sueltas, texto limitado + tooltip. -->
          <div v-if="groupOptions.length" class="pf-groups">
            <button v-for="a in groupOptions" :key="a.name" class="pf-group" :class="{ active: groupFilter === a.name }"
              :style="groupPillStyle(a)" v-tip="a.name" @click="toggleGroup(a.name)">{{ a.name }}</button>
          </div>

          <!-- Filtro por estado general: selector. -->
          <select v-model="statusFilter" class="pf-state">
            <option v-for="s in statuses" :key="s.key" :value="s.key">{{ $t(s.label) }}</option>
          </select>

          <button v-if="hasFilters" class="pf-clear" @click="clearFilters">
            <b-icon pack="fas" icon="xmark" size="is-small"></b-icon>{{ $t('prm_portfolio_clear_filters') }}
          </button>

          <button class="source-btn" :class="{ active: sourceMode }" @click="sourceMode = !sourceMode">
            <b-icon pack="fas" icon="database" size="is-small"></b-icon>
            <span>{{ $t('prm_summary_source_button') }}</span>
          </button>
        </div>
      </div>

      <div v-if="sourceMode" class="source-banner">
        <b-icon pack="fas" icon="circle-info" size="is-small"></b-icon>
        <span v-html="$t('prm_summary_source_hint')"></span>
      </div>

      <div class="pf-count">
        {{ $t('prm_portfolio_showing', [filtered.length, allRows.length]) }}
        <span v-if="groupFilter" class="pf-count-group">· {{ groupFilter }}
          <button class="pf-count-clear" @click="groupFilter = null">✕</button>
        </span>
      </div>

      <div class="pf-body">
        <!-- KPIs sobre el portfolio completo; las tablas de abajo respetan los filtros. -->
        <kpi-summary :kpis="kpis" :subtitle="kpiSubtitle" :mode="sourceMode"></kpi-summary>

        <critical-projects :all-rows="filtered" :mode="sourceMode" @open="openProject"></critical-projects>

        <contractual-risk :all-rows="filtered" :mode="sourceMode" @open="openProject"></contractual-risk>

        <projects-table :rows="filtered" :current-project-id="currentProjectId" :subtitle="tableSubtitle"
          :mode="sourceMode" @open="openProject"></projects-table>

        <group-aggregates :groups="groups" :active-group="groupFilter" :subtitle="$t('prm_portfolio_groups_subtitle')"
          :mode="sourceMode" @toggle-group="toggleGroup"></group-aggregates>

        <criteria-legends></criteria-legends>
      </div>
    </template>
  </div>
</template>

<script>
  import axios from 'axios'
  import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
  import KpiSummary from './kpi-summary.vue'
  import CriticalProjects from './critical-projects.vue'
  import ContractualRisk from './contractual-risk.vue'
  import ProjectsTable from './projects-table.vue'
  import GroupAggregates from './group-aggregates.vue'
  import CriteriaLegends from './criteria-legends.vue'
  import { normalize, portfolioKpis, groupAggregates, semState, riskCritical } from './portfolio-model'
  import { tip } from '../shared/tip'

  export default {
    name: 'PrmPortfolioTab',

    components: { ScaleLoader, KpiSummary, CriticalProjects, ContractualRisk, ProjectsTable, GroupAggregates, CriteriaLegends },

    directives: { tip },

    props: {
      company: { type: Object, required: true },
      currentProjectId: { type: Number, default: null },
    },

    data: () => ({
      loading: true,
      sourceMode: false,
      statusFilter: 'all',
      groupFilter: null,
      search: '',
      allRows: [],
      statuses: [
        { key: 'all', label: 'prm_portfolio_status_all' },
        { key: 'green', label: 'prm_portfolio_status_green' },
        { key: 'amber', label: 'prm_portfolio_status_amber' },
        { key: 'red', label: 'prm_portfolio_status_red' },
        { key: 'critical', label: 'prm_portfolio_status_critical' },
        { key: 'contractual_risk', label: 'prm_portfolio_status_contractual' },
      ],
    }),

    computed: {
      // Filas que pasan búsqueda + estado (sin aplicar aún el filtro de grupo).
      baseFiltered () {
        return this.allRows.filter((r) => this.searchMatch(r) && this.statusMatch(r))
      },
      // Tabla de proyectos y KPIs: además del filtro de grupo si hay uno activo.
      filtered () {
        return this.groupFilter ? this.baseFiltered.filter((r) => r.group === this.groupFilter) : this.baseFiltered
      },
      // KPIs sobre el portfolio COMPLETO (resumen ejecutivo estable, no depende de los filtros).
      kpis () { return portfolioKpis(this.allRows) },
      // La tabla de grupos muestra todas las grupos del conjunto filtrado (para poder elegir una).
      groups () { return groupAggregates(this.baseFiltered) },
      // Grupos únicos (para las pastillas de filtro), con su color real, ordenados por nombre.
      groupOptions () {
        const map = new Map()
        this.allRows.forEach((r) => { if (r.group && !map.has(r.group)) map.set(r.group, r.groupColor) })
        return [...map.entries()].map(([name, color]) => ({ name, color })).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
      },
      hasFilters () {
        return this.statusFilter !== 'all' || this.groupFilter !== null || this.search.trim() !== ''
      },

      kpiSubtitle () {
        return this.$t('prm_portfolio_kpis_subtitle', [this.allRows.length])
      },
      tableSubtitle () {
        return this.$t('prm_portfolio_projects_subtitle')
      },
    },

    watch: {
      'company.id': { immediate: true, handler () { this.load() } },
    },

    methods: {
      async load () {
        if (!this.company || !this.company.id) return
        this.loading = true
        try {
          const { data } = await axios.get(`/api/v2/company/${this.company.id}/dashboard-prm/portfolio`, {
            params: { currentProjectId: this.currentProjectId },
          })
          this.allRows = (data.data.projects || []).map(normalize)
        } catch (e) {
          this.$notify.error((e && e.error_msg) || this.$t('error_retrieving_data'))
        } finally {
          this.loading = false
        }
      },

      searchMatch (r) {
        const q = this.search.trim().toLowerCase()
        if (!q) return true
        return [r.code, r.name, r.client, r.pm, r.group].some((s) => (s || '').toLowerCase().includes(q))
      },
      statusMatch (r) {
        if (this.statusFilter === 'all') return true
        if (this.statusFilter === 'contractual_risk') return riskCritical(r)
        // "Situación crítica (**)" coincide con estado rojo; el diseño lista ambas opciones.
        if (this.statusFilter === 'critical') return semState(r) === 'red'
        return semState(r) === this.statusFilter
      },

      toggleGroup (group) {
        this.groupFilter = this.groupFilter === group ? null : group
      },
      // Pastilla de grupo: fondo sólido del color, texto blanco; atenuada si hay otra activa.
      groupPillStyle (a) {
        const dim = this.groupFilter !== null && this.groupFilter !== a.name
        return { background: a.color, color: '#fff', opacity: dim ? 0.4 : 1 }
      },
      clearFilters () {
        this.statusFilter = 'all'
        this.groupFilter = null
        this.search = ''
      },

      // Abre el Dashboard PRM del proyecto: lo delegamos al padre (cambia el proyecto activo del store).
      openProject (r) {
        this.$emit('open', r.project)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .portfolio-tab { min-width: 0; }
  .pf-loading { display: flex; justify-content: center; padding: 60px 0; }

  .pf-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
  .pf-subtitle { font-size: 12px; color: #6b7787; }

  // Controles alineados a la derecha (buscador · pastillas de grupo · estado · limpiar · fuente).
  .pf-controls { margin-left: auto; display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }

  .pf-search {
    display: inline-flex; align-items: center; gap: 6px; border: 1px solid #d4dae3; background: #fff;
    border-radius: 6px; padding: 3px 8px; color: #94a3b8;
  }
  .pf-search input { border: none; outline: none; font-size: 12px; color: #1e3046; width: 150px; }

  .pf-groups { display: inline-flex; align-items: center; gap: 4px; flex-wrap: wrap; }
  .pf-group {
    max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; border: none;
    color: #fff; font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 4px; cursor: pointer;
    transition: opacity .1s;
  }
  .pf-group.active { outline: 2px solid #3a6bc8; outline-offset: 1px; }

  .pf-state {
    border: 1px solid #d4dae3; background: #fff; color: #1e3046; font-size: 11px; font-weight: 600;
    padding: 4px 8px; border-radius: 6px; cursor: pointer; outline: none;
  }
  .pf-clear {
    display: inline-flex; align-items: center; gap: 4px; border: 1px solid #d4dae3; background: #fff;
    color: #6b7787; font-size: 10px; font-weight: 600; padding: 4px 8px; border-radius: 6px; cursor: pointer;
  }
  .pf-clear:hover { color: #1e3046; background: #f5f7fa; }

  .source-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid #e0b52a; background: #fff; color: #8a6d00;
    font-size: 11px; font-weight: 600; padding: 5px 10px; border-radius: 6px; cursor: pointer;
  }
  .source-btn.active { background: #fff4d6; }
  .source-btn.active:hover { background: #fbecc0; }

  .source-banner {
    display: flex; align-items: flex-start; gap: 8px; margin-bottom: 10px;
    border: 1px solid #e0b52a; background: #fff4d6; color: #8a6d00;
    border-radius: 6px; padding: 8px 12px; font-size: 11px; line-height: 1.45;
  }
  .source-banner ::v-deep b { font-weight: 700; }

  .pf-count { font-size: 11px; color: #6b7787; margin-bottom: 10px; }
  .pf-count-group { color: #2b5cad; font-weight: 600; }
  .pf-count-clear {
    border: none; background: transparent; color: #2b5cad; cursor: pointer; font-size: 11px; padding: 0 2px;
  }

  .pf-body { display: flex; flex-direction: column; gap: 12px; }
</style>
